// ts-rest contract for the Desk Dashboard (the IM's landing page).
//
// A per-view presentation projection of the IM's book: per-client AUM + YTD
// (from the ValuationGrid holdings, valued live via Yahoo) set against the ARC
// PCI benchmark for the client's risk band, plus an AUM-band segment estimate.
// Scoped server-side to the signed-in IM (Client.investmentManagerUserId); a
// firm-wide actor (ops / view / admin) sees the whole book. NOT a domain model;
// the controller maps ctx.read(...) + the shared valuation/benchmark engine
// onto these DTOs.
//
// Endpoint under /api/desk/*:
//   GET /api/desk/dashboard — book stats + per-client rows
//   GET /api/desk/dashboard/overview — top holdings, meetings, due reviews,
//       call action points and breaches

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

/**
 * Performance window the dashboard's return figures are computed over.
 *
 * `ytd` reads the live per-ISIN Yahoo YTD. The calendar windows read the
 * monthly per-account returns export (SEP-199, SEP-200), which is why they are
 * as at a month end and not as at today. `custom` has no per-account source at
 * all: it drives the benchmark series alone, and the book side reports why it
 * is absent (ADR-0009: no fabricated returns). See the service for exactly what
 * each window computes.
 */
export const deskPerformanceRangeSchema = z.enum(['1m', '6m', '1y', '3y', 'ytd', 'custom']);
export type DeskPerformanceRange = z.infer<typeof deskPerformanceRangeSchema>;

export const deskDashboardStatsSchema = z.object({
    clientCount: z.number().int(),
    /**
     * AUM = discretionary-mandate security holdings — the SAME per-account,
     * cash-excluded definition as the Desk Portfolio "Total AUM", so the two
     * surfaces agree (DESK2). Not the per-person serviceType sum.
     */
    totalAumGbp: z.number(),
    /** AUA = Advisory + Execution-Only mandate security holdings. */
    totalAuaGbp: z.number(),
    /** Advisory-mandate security holdings (the Advisory slice of AUA). */
    totalAdvisoryGbp: z.number(),
    /** Execution-only mandate security holdings (the XO slice of AUA). */
    totalXoGbp: z.number(),
    /** Whole-book mandated security value (AUM + Advisory + XO; cash excluded). */
    grandTotalGbp: z.number(),
    /** Clients whose AUM-band estimate is Ultra High Net Worth. */
    uhnwCount: z.number().int(),
    /** AUM-weighted book YTD across priced holdings, or null if none priced. */
    avgYtdPct: z.number().nullable(),
    /** AUM-weighted ARC benchmark YTD for the book, or null if no benchmark. */
    avgBenchmarkYtdPct: z.number().nullable(),
    /** The ARC benchmark month-end the comparison is "as at", or null. */
    benchmarkAsOf: z.string().nullable(),
});
export type DeskDashboardStats = z.infer<typeof deskDashboardStatsSchema>;

/**
 * Which source produced the book return for the requested window (SEP-200).
 *
 * The dashboard has three sources, and they are three different methodologies.
 * The card names the one that answered, so a reader never compares two windows
 * that were measured in different ways without a warning.
 *
 * - `modified_dietz` — the SEP-187 methodology over the month-end valuation
 *   series. It wins whenever the series covers the whole window.
 * - `ytd_holdings` — the live per-holding YTD from the ValuationGrid, priced
 *   through Yahoo. YTD only.
 * - `returns_export` — the monthly per-account returns export behind the
 *   Benchmarking page (SEP-199).
 * - `none` — no source covers the window. `unavailableReason` states why.
 */
export const deskBookReturnSourceSchema = z.enum([
    'modified_dietz',
    'ytd_holdings',
    'returns_export',
    'none',
]);
export type DeskBookReturnSource = z.infer<typeof deskBookReturnSourceSchema>;

/**
 * The book and benchmark returns over the requested window.
 *
 * The benchmark side comes from the ARC PCI level series (real where the levels
 * span the window). The book side comes from whichever source in
 * `deskBookReturnSourceSchema` covers the window. Both figures carry their
 * coverage, because a return that covers part of the book is not the book's
 * return: the card states the count instead of an unqualified percentage.
 */
export const deskBenchmarkRangeSchema = z.object({
    /** The window actually computed (echoes the request; `custom` carries the dates). */
    range: deskPerformanceRangeSchema,
    /** The book's own AUM-weighted return % over the window (range-aware). */
    bookReturnPct: z.number().nullable(),
    /** Which methodology produced `bookReturnPct`. `none` when nothing did. */
    bookReturnSource: deskBookReturnSourceSchema,
    /** Clients that contributed to `bookReturnPct` (a real figure and a weight). */
    coveredClients: z.number().int(),
    /** Clients in the book the window was asked for. */
    totalClients: z.number().int(),
    /** Why `bookReturnPct` is null, in the reader's words. Null when it is not. */
    unavailableReason: z.string().nullable(),
    /**
     * The date the book return is AS AT: the export's reporting month
     * (`YYYY-MM`) or the window's last month. Null when the source is live, and
     * null when no source answered. The card shows it, because a monthly figure
     * that carries no date reads as a live one.
     */
    bookReturnAsOf: z.string().nullable(),
    /** AUM-weighted ARC benchmark return % over the window, or null if uncomputable. */
    benchmarkReturnPct: z.number().nullable(),
    /** The window's start month-end the return is measured from, or null. */
    fromAsOf: z.string().nullable(),
    /** The window's end month-end the return is measured to, or null. */
    toAsOf: z.string().nullable(),
});
export type DeskBenchmarkRange = z.infer<typeof deskBenchmarkRangeSchema>;

export const deskDashboardClientRowSchema = z.object({
    clientId: z.string(),
    fullName: z.string(),
    riskRating: z.string().nullable(),
    serviceType: z.string().nullable(),
    /** AUM-band estimate (never the canonical segment, which the grid leaves null). */
    segment: z.string().nullable(),
    /** True — the segment is an AUM-band estimate, for the UI to label. */
    segmentEstimated: z.boolean(),
    aumGbp: z.number().nullable(),
    /**
     * The Household (family group) of the client, or null when the client is in
     * no household. The page counts the family groups of the visible rows from
     * this field, so the count follows the service-type chips.
     */
    householdId: z.string().nullable(),
    ytdPct: z.number().nullable(),
    /** The ARC band this client is benchmarked against, for example "BalancedAsset". */
    arcBand: z.string().nullable(),
    benchmarkYtdPct: z.number().nullable(),
    /**
     * The client's own return % over the REQUESTED window (SEP-200), or null
     * when no source covers it. The page weights these into the performance
     * card, so the card always agrees with the rows the filter chips show.
     */
    rangeReturnPct: z.number().nullable(),
    /** The ARC band's return % over the same window, or null. */
    benchmarkRangeReturnPct: z.number().nullable(),
});
export type DeskDashboardClientRow = z.infer<typeof deskDashboardClientRowSchema>;

export const deskDashboardResponseSchema = z.object({
    stats: deskDashboardStatsSchema,
    clients: z.array(deskDashboardClientRowSchema),
    /** The IM the book is scoped to, or null for a firm-wide (ops/view/admin) read. */
    scopedToImUserId: z.string().nullable(),
    /** The benchmark's return over the requested performance window (range-aware). */
    benchmarkRange: deskBenchmarkRangeSchema,
});
export type DeskDashboardResponse = z.infer<typeof deskDashboardResponseSchema>;

// ---------------------------------------------------------------------------
// The overview: the work and the exposure of the book, beside the figures above.
// ---------------------------------------------------------------------------

/**
 * One line of the largest discretionary holdings of the book, aggregated by
 * ISIN. It is the same aggregate as the Desk Portfolio holdings list, so the
 * two surfaces agree.
 */
export const deskDashboardHoldingSchema = z.object({
    isin: z.string(),
    securityName: z.string(),
    valuationGbp: z.number(),
    /** The share of the discretionary book value, in percent. */
    weightPct: z.number(),
    /** The number of portfolios of the book that hold the line. */
    portfolioCount: z.number().int(),
});
export type DeskDashboardHolding = z.infer<typeof deskDashboardHoldingSchema>;

/** One event of the Outlook calendar of the IM that names a client of the book. */
export const deskDashboardMeetingSchema = z.object({
    id: z.string(),
    /** The ISO-8601 UTC start of the event. */
    startsAt: z.string(),
    clientName: z.string(),
    subject: z.string(),
});
export type DeskDashboardMeeting = z.infer<typeof deskDashboardMeetingSchema>;

/**
 * The upcoming client meetings. The source is the live Outlook calendar of the
 * signed-in user. `calendarConnected` is false when the user has no Microsoft
 * 365 connection or the read failed, so the page can say why the list is empty.
 */
export const deskDashboardMeetingsSchema = z.object({
    calendarConnected: z.boolean(),
    entries: z.array(deskDashboardMeetingSchema),
});

/**
 * Where the renewal date of a fact find comes from.
 *
 * - `septima` — the latest approved Septima fact find, plus 18 months.
 * - `crm` — the "Fact Find Expiry Date" of the Dynamics import.
 * - `document` — the review due date that a confirmed fact-find upload stamps.
 */
export const deskFactFindRenewalSourceSchema = z.enum(['septima', 'crm', 'document']);

/** One client whose fact find is due for renewal in the window, or is overdue. */
export const deskDashboardFactFindRenewalSchema = z.object({
    clientId: z.string(),
    clientName: z.string(),
    /** The renewal date, `YYYY-MM-DD`. A date before today is overdue. */
    dueDate: z.string(),
    source: deskFactFindRenewalSourceSchema,
});
export type DeskDashboardFactFindRenewal = z.infer<typeof deskDashboardFactFindRenewalSchema>;

/** One completed vulnerability assessment whose review is due in the window, or overdue. */
export const deskDashboardVulnerabilityReviewSchema = z.object({
    assessmentId: z.string(),
    clientId: z.string().nullable(),
    subjectName: z.string(),
    /** The next review date, `YYYY-MM-DD`: the completion date plus the band frequency. */
    dueDate: z.string(),
    /** The RAG band of the assessment, for example `amber`. */
    band: z.string(),
});
export type DeskDashboardVulnerabilityReview = z.infer<
    typeof deskDashboardVulnerabilityReviewSchema
>;

/** One open action point that ATLAS took from a client call. */
export const deskDashboardActionPointSchema = z.object({
    id: z.string(),
    callId: z.string(),
    clientId: z.string().nullable(),
    clientName: z.string().nullable(),
    title: z.string(),
    owner: z.string(),
    /** The due date in the words of the call, for example "by end of week". */
    dueDescription: z.string(),
    /** The ISO start of the source call, or null when the call is not readable. */
    callDate: z.string().nullable(),
    /** True while the IM has not yet approved the draft that ATLAS proposed. */
    awaitingReview: z.boolean(),
});
export type DeskDashboardActionPoint = z.infer<typeof deskDashboardActionPointSchema>;

/**
 * What kind of breach a row reports.
 *
 * - `allocation` — an open deviation from the house model portfolio (PCF).
 * - `risk` — an open suitability alert against the risk profile, the capacity
 *   for loss, the mandate or the concentration limit.
 * - `risk_limit` — the equity share of the discretionary holdings is above the
 *   limit of the risk profile of the client (fact-find explainer EXP-33).
 * - `allocation_limit` — the fixed-income share or the cash share is above the
 *   limit of the risk profile of the client.
 *
 * The first two kinds are open cases. The two limit kinds are a live check of
 * the holdings of today: they have no case row and no date.
 */
export const deskDashboardBreachKindSchema = z.enum([
    'allocation',
    'risk',
    'risk_limit',
    'allocation_limit',
]);

/** One open breach of a client of the book. */
export const deskDashboardBreachSchema = z.object({
    id: z.string(),
    kind: deskDashboardBreachKindSchema,
    clientId: z.string(),
    clientName: z.string(),
    /** A short statement of the breach, for example "Equity over the model band". */
    summary: z.string(),
    /** The severity as its source states it, for example `material`. */
    severity: z.string(),
    /** When the case opened. Null for a limit breach, which is a live check. */
    raisedAt: z.string().nullable(),
});
export type DeskDashboardBreach = z.infer<typeof deskDashboardBreachSchema>;

/**
 * The overview. Each part after `topHoldings` belongs to a feature, and the
 * key is absent when the Feature Manifest turns that feature off (ADR-0027).
 * An absent key is not an empty list: the deployment does not run the part.
 */
export const deskDashboardOverviewSchema = z.object({
    /** The number of days ahead that the due lists cover. */
    windowDays: z.number().int(),
    /** The largest discretionary holdings, largest first. At most 30. */
    topHoldings: z.array(deskDashboardHoldingSchema),
    /** `wealth.ai.calls-meetings`. */
    meetings: deskDashboardMeetingsSchema.optional(),
    /** `wealth.onboarding.fact-find.freshness`. */
    factFindRenewals: z.array(deskDashboardFactFindRenewalSchema).optional(),
    /** `wealth.onboarding.vulnerability`. */
    vulnerabilityReviews: z.array(deskDashboardVulnerabilityReviewSchema).optional(),
    /** `wealth.ai.calls-meetings.actions`. */
    actionPoints: z.array(deskDashboardActionPointSchema).optional(),
    /** `wealth.compliance.mandate-breaches`. */
    breaches: z.array(deskDashboardBreachSchema).optional(),
});
export type DeskDashboardOverview = z.infer<typeof deskDashboardOverviewSchema>;

export const deskDashboardContract = c.router(
    {
        getDashboard: {
            method: 'GET',
            path: '/desk/dashboard',
            query: z
                .object({
                    /** Performance window for the range-aware benchmark return. Defaults to YTD. */
                    range: deskPerformanceRangeSchema.optional(),
                    /** Custom window start (YYYY-MM-DD), used only when range is `custom`. */
                    from: z.string().optional(),
                    /** Custom window end (YYYY-MM-DD), used only when range is `custom`. */
                    to: z.string().optional(),
                })
                .optional(),
            responses: { 200: deskDashboardResponseSchema },
            summary: "The signed-in IM's book: AUM/AUA, YTD vs ARC benchmark, segment mix",
        },
        getOverview: {
            method: 'GET',
            path: '/desk/dashboard/overview',
            responses: { 200: deskDashboardOverviewSchema },
            summary:
                "The signed-in IM's top holdings, meetings, due reviews, action points and breaches",
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
